import type { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core'
import { Button } from '@trellis/roots/button'
import { RichTextRenderer, RichTextEditor } from '@trellis/roots/data'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@trellis/roots/dropdown-menu'
import { CurrencyInput, QuantitySelector } from '@trellis/roots/forms'
import { Label } from '@trellis/roots/label'
import { Switch } from '@trellis/roots/switch'
import { Typography } from '@trellis/roots/typography'
import type { LineItemDiscountType } from '@trellis/shared'
import {
  computeLineItemSubtotal,
  getQuantityStepForUnitType,
  isRichTextEmpty,
} from '@trellis/shared'
import { ChevronDown, ChevronUp, GripVertical, MoreHorizontal, X } from 'lucide-react'
import { useId, useState } from 'react'

import { UnitTypeCombobox } from '@/components/unit-type-combobox'
import { getCurrencyFormatter } from '@/lib/format'
import { cn } from '@/lib/utils'

import { LineItemTitleCombobox } from './line-item-title-combobox'

export interface AdHocRowValue {
  type: 'adhoc'
  id?: string
  title: string
  description: string | null
  unitPrice: number
  quantity: number
  unitType: string
  discountType: LineItemDiscountType
  discount: number
  taxable: boolean
  saveToRegistry?: boolean
}

export interface RegistryRowValue {
  type: 'registry'
  id?: string
  lineItemId: string
  // Read-only registry fields for display and subtotal computation
  title: string
  unitPrice: number
  unitType: string
  description: string | null
  taxable: boolean
  // Editable override fields
  quantity: number
  discountType: LineItemDiscountType
  discount: number
}

export type LineItemRowValue = AdHocRowValue | RegistryRowValue

interface LineItemDtoInput {
  id: string
  lineItemId?: string | null
  isDetached: boolean
  title: string
  unitPrice: number
  unitType: string
  description?: string | null
  taxable: boolean
  quantity: number
  discountType: LineItemDiscountType
  discount: number
}

export function toLineItemRowValue(lineItem: LineItemDtoInput): LineItemRowValue {
  if (lineItem.lineItemId && !lineItem.isDetached) {
    return {
      id: lineItem.id,
      type: 'registry',
      lineItemId: lineItem.lineItemId,
      title: lineItem.title,
      unitPrice: lineItem.unitPrice,
      unitType: lineItem.unitType,
      description: lineItem.description ?? null,
      taxable: lineItem.taxable,
      quantity: lineItem.quantity,
      discountType: lineItem.discountType,
      discount: lineItem.discount,
    }
  }
  return {
    id: lineItem.id,
    type: 'adhoc',
    title: lineItem.title,
    description: lineItem.description ?? null,
    unitPrice: lineItem.unitPrice,
    unitType: lineItem.unitType,
    quantity: lineItem.quantity,
    discountType: lineItem.discountType,
    discount: lineItem.discount,
    taxable: lineItem.taxable,
  }
}

const DISCOUNT_TYPE_OPTIONS = [
  { value: 'PERCENTAGE', label: '% Percentage' },
  { value: 'FIXED', label: '$ Fixed' },
] as const

interface LineItemRowProps {
  value: LineItemRowValue
  index: number
  currency: string
  errors: Record<string, string | undefined>
  onChange: (value: LineItemRowValue) => void
  onRemove: () => void
  isExpanded: boolean
  onToggleExpand: () => void
  /**
   * Whether the surrounding form is editing a previously-saved document, not creating a
   * new one.
   */
  isEditMode: boolean
  dragListeners?: DraggableSyntheticListeners
  dragAttributes?: DraggableAttributes
}

export function LineItemRow({
  value,
  index,
  currency,
  errors,
  onChange,
  onRemove,
  isExpanded,
  onToggleExpand,
  isEditMode,
  dragListeners,
  dragAttributes,
}: LineItemRowProps) {
  const isRegistry = value.type === 'registry'
  // Registry-derived fields are read-only until the document is saved and reopened for
  // editing — matches the "You can edit this line item after saving" hint below.
  const isRegistryLocked = isRegistry && !isEditMode

  const [isDiscountSectionOpen, setIsDiscountSectionOpen] = useState(
    value.discountType !== 'NONE',
  )
  const rowId = useId()
  const [isTaxableSectionOpen, setIsTaxableSectionOpen] = useState(
    !isRegistry && !value.taxable,
  )

  const subtotal = computeLineItemSubtotal(
    isNaN(value.unitPrice) ? 0 : value.unitPrice,
    isNaN(value.quantity) ? 0 : value.quantity,
    isNaN(value.discount) ? 0 : value.discount,
    value.discountType,
  )

  const currencyFormatter = getCurrencyFormatter(currency)

  function handleEditableChange<
    K extends
      | 'title'
      | 'description'
      | 'unitPrice'
      | 'unitType'
      | 'quantity'
      | 'discount'
      | 'discountType'
      | 'taxable',
  >(field: K, fieldValue: LineItemRowValue[K]) {
    onChange({ ...value, [field]: fieldValue })
  }

  function handleAddDiscount() {
    setIsDiscountSectionOpen(true)
    onChange({ ...value, discountType: 'PERCENTAGE', discount: 0 })
  }

  function handleRemoveDiscount() {
    setIsDiscountSectionOpen(false)
    onChange({ ...value, discountType: 'NONE', discount: 0 })
  }

  function handleMarkNonTaxable() {
    setIsTaxableSectionOpen(true)
    handleEditableChange('taxable', false)
  }

  function handleRestoreTaxable() {
    setIsTaxableSectionOpen(false)
    handleEditableChange('taxable', true)
  }

  const displayTitle = value.title || 'Untitled item'
  const hasDiscount =
    value.discountType !== 'NONE' && !isNaN(value.discount) && value.discount > 0
  // Rows saved before this control existed carry discountType 'NONE' — render that as
  // Percentage (the switch's unchecked state) since discount defaults to 0, a no-op either way.
  const isDiscountFixed = value.discountType === 'FIXED'

  const discountLabel = hasDiscount
    ? value.discountType === 'PERCENTAGE'
      ? `${value.discount}% off`
      : `${currencyFormatter.format(value.discount)} off`
    : null

  const unitPrice = isNaN(value.unitPrice) ? 0 : value.unitPrice
  const quantity = isNaN(value.quantity) ? 0 : value.quantity

  return (
    <div className="rounded-md border">
      {/* Collapsed header — always visible */}
      <div className="flex items-center gap-2 px-3 py-2.5">
        {dragListeners && (
          <button
            type="button"
            aria-label={`Reorder item ${index + 1}`}
            className="text-muted-foreground/50 hover:text-muted-foreground shrink-0 cursor-grab touch-none active:cursor-grabbing"
            {...dragAttributes}
            {...dragListeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        )}

        <button
          type="button"
          onClick={onToggleExpand}
          aria-label={isExpanded ? 'Collapse item' : 'Expand item'}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
        >
          <span className="min-w-0 flex-1 truncate text-sm font-medium">
            {displayTitle}
          </span>
          <div className="text-muted-foreground flex shrink-0 items-center gap-2 text-xs">
            {quantity > 0 && unitPrice > 0 && (
              <span className="tabular-nums">
                {quantity} × {currencyFormatter.format(unitPrice)}
              </span>
            )}
            {discountLabel && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                {discountLabel}
              </span>
            )}
            {(quantity > 0 || unitPrice > 0) && (
              <span className="text-foreground font-semibold tabular-nums">
                {currencyFormatter.format(subtotal)}
              </span>
            )}
          </div>
          <span className="hover:bg-accent hover:text-accent-foreground text-muted-foreground flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors">
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </span>
        </button>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          aria-label={`Remove item ${index + 1}`}
          onClick={onRemove}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t px-4 pt-3 pb-4">
          <div className="space-y-3">
            {/* Title */}
            <div className="space-y-1">
              <Label htmlFor={`lineItems[${index}].title`}>Title</Label>
              {isRegistryLocked ? (
                <p className="border-input bg-muted rounded-md border px-3 py-2 text-sm">
                  {value.title}
                </p>
              ) : (
                <LineItemTitleCombobox
                  id={`lineItems[${index}].title`}
                  value={value.title}
                  onChange={(title) => handleEditableChange('title', title)}
                  onSelectSuggestion={(suggestion) => onChange(suggestion)}
                  autoFocus={value.title === ''}
                />
              )}
              {errors.title && (
                <p className="text-destructive-text text-xs">{errors.title}</p>
              )}
            </div>

            {/* Description — hidden for locked registry items with no description set */}
            {(!isRegistryLocked || !isRichTextEmpty(value.description)) && (
              <div className="space-y-1">
                <Label htmlFor={`lineItems[${index}].description`}>Description</Label>
                {isRegistryLocked ? (
                  <RichTextRenderer
                    content={value.description}
                    className="text-muted-foreground text-sm"
                  />
                ) : (
                  <RichTextEditor
                    id={`lineItems[${index}].description`}
                    value={value.description}
                    onChange={(html) => handleEditableChange('description', html)}
                    placeholder="Optional"
                  />
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {/* Unit Price */}
              <div className="space-y-1">
                <Label htmlFor={`lineItems[${index}].unitPrice`}>Unit Price</Label>
                {isRegistryLocked ? (
                  <p className="border-input bg-muted rounded-md border px-3 py-2 text-sm">
                    {currencyFormatter.format(value.unitPrice)}
                  </p>
                ) : (
                  <CurrencyInput
                    id={`lineItems[${index}].unitPrice`}
                    value={value.unitPrice}
                    onChange={(unitPriceValue) =>
                      handleEditableChange('unitPrice', unitPriceValue)
                    }
                    currency={currency}
                    min={0}
                  />
                )}
                {errors.unitPrice && (
                  <p className="text-destructive-text text-xs">{errors.unitPrice}</p>
                )}
              </div>

              {/* Quantity */}
              <div className="space-y-1">
                <Label htmlFor={`lineItems[${index}].quantity`}>Quantity</Label>
                <QuantitySelector
                  id={`lineItems[${index}].quantity`}
                  value={value.quantity}
                  step={getQuantityStepForUnitType(value.unitType)}
                  onChange={(quantity) => handleEditableChange('quantity', quantity)}
                  className="w-full"
                  inputClassName="flex-1"
                />
                {errors.quantity && (
                  <p className="text-destructive-text text-xs">{errors.quantity}</p>
                )}
              </div>

              {/* Unit Type */}
              <div className="space-y-1">
                <Label htmlFor={`lineItems[${index}].unitType`}>Unit</Label>
                {isRegistryLocked ? (
                  <p className="border-input bg-muted rounded-md border px-3 py-2 text-sm">
                    {value.unitType}
                  </p>
                ) : (
                  <UnitTypeCombobox
                    id={`lineItems[${index}].unitType`}
                    value={value.unitType}
                    onChange={(selectedValue) =>
                      handleEditableChange('unitType', selectedValue)
                    }
                  />
                )}
              </div>
            </div>

            {/* Discount — opt-in via the "More options" menu below */}
            {isDiscountSectionOpen && (
              <div className="bg-muted/40 space-y-2 rounded-md border border-dashed p-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor={`lineItems[${index}].discount`}>Discount</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    aria-label={`Remove discount for item ${index + 1}`}
                    onClick={handleRemoveDiscount}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex w-fit gap-1 rounded-lg border p-1">
                    {DISCOUNT_TYPE_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() =>
                          onChange({
                            ...value,
                            discountType: option.value as LineItemDiscountType,
                            discount: 0,
                          })
                        }
                        aria-label={`Set discount type to ${option.label} for item ${index + 1}`}
                        className={cn(
                          'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                          value.discountType === option.value
                            ? 'bg-foreground text-background'
                            : 'text-muted-foreground hover:text-foreground',
                        )}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center gap-3">
                    {isDiscountFixed ? (
                      <CurrencyInput
                        id={`lineItems[${index}].discount`}
                        value={value.discount}
                        onChange={(discountValue) =>
                          handleEditableChange('discount', discountValue)
                        }
                        currency={currency}
                        min={0}
                        ariaLabel={`Discount amount for item ${index + 1}`}
                      />
                    ) : (
                      <QuantitySelector
                        id={`lineItems[${index}].discount`}
                        value={value.discount}
                        min={0}
                        max={100}
                        step={1}
                        onChange={(discountValue) =>
                          handleEditableChange('discount', discountValue)
                        }
                        ariaLabel={`Discount percentage for item ${index + 1}`}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Non-taxable override — ad-hoc only, opt-in via the "More options" menu below */}
            {!isRegistry && isTaxableSectionOpen && (
              <div className="bg-muted/40 flex items-center justify-between rounded-md border border-dashed px-3 py-2">
                <Typography variant="body-sm" className="font-medium">
                  Marked non-taxable
                </Typography>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  aria-label={`Remove non-taxable override for item ${index + 1}`}
                  onClick={handleRestoreTaxable}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              {/* More options — reveals Discount / Taxable only when needed */}
              {(!isDiscountSectionOpen || (!isRegistry && !isTaxableSectionOpen)) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      aria-label={`More options for item ${index + 1}`}
                    >
                      <MoreHorizontal className="mr-1 h-4 w-4" />
                      More options
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {!isDiscountSectionOpen && (
                      <DropdownMenuItem onClick={handleAddDiscount}>
                        Add discount
                      </DropdownMenuItem>
                    )}
                    {!isRegistry && !isTaxableSectionOpen && (
                      <DropdownMenuItem onClick={handleMarkNonTaxable}>
                        Mark as non-taxable
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {value.type === 'adhoc' && (
                <div className="flex items-center gap-2">
                  <Switch
                    id={`${rowId}-save-to-registry`}
                    checked={value.saveToRegistry ?? false}
                    onCheckedChange={(checked) =>
                      onChange({ ...value, saveToRegistry: checked })
                    }
                  />
                  <Label htmlFor={`${rowId}-save-to-registry`} className="font-normal">
                    Save to registry
                  </Label>
                </div>
              )}
            </div>

            {/* Registry hint */}
            {isRegistryLocked && (
              <p className="text-muted-foreground text-xs italic">
                You can edit this line item after saving.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
